// Fatia S5a: assistente de criação de campanha em 5 passos (Objetivo → Público → Anúncio → Orçamento → Revisão).
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, RefreshCw, Save } from 'lucide-react'
import { brl } from '@impulso/shared'
import type { BlueprintDto, BlueprintPatch, DraftDto } from '@impulso/shared/api/builder'
import { ApiError, errorMessage } from '../../lib/api'
import { useCan, useCompany } from '../../lib/company'
import { Empty, ErrorState, Loading, PageHead, Tabs } from '../../ui'
import { useToast } from '../../ui/toast'
import { useAssets } from '../../features/assets/api'
import { useBuilderOptions, useCreateDraft, useDraft, useEstimate, usePublish, useSaveDraft, useValidate } from '../../features/builder/api'
import { STEPS, initialBlueprint } from '../../features/builder/constants'
import { GooglePreview, InstagramPreview } from '../../features/builder/Previews'
import { AdStep, AudienceStep, BudgetStep, GoalStep, targetPlatforms } from '../../features/builder/steps'
import { DraftsList, PublishStatus, ReviewStep, estimateText } from '../../features/builder/review'

function toPatch(bp: BlueprintDto): BlueprintPatch {
  return { goal: bp.goal, name: bp.name.trim() || 'Nova campanha', offer: bp.offer, audience: bp.audience, creative: bp.creative, budget: bp.budget, destination: bp.destination ?? {} }
}

function daysOf(bp: BlueprintDto) {
  if (!bp.budget.endDate) return 30
  const start = bp.budget.startDate ? Date.parse(`${bp.budget.startDate}T00:00:00`) : Date.now()
  return Math.max(1, Math.round((Date.parse(`${bp.budget.endDate}T00:00:00`) - start) / 86_400_000))
}

export default function NewCampaign() {
  const company = useCompany()
  const companyId = company.id
  const { draftId } = useParams()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()
  const canEdit = useCan('company.edit_drafts')
  const canPublish = useCan('company.publish')

  const renew = params.get('renovar')
  const offerParam = params.get('oferta')
  const step = Math.min(STEPS.length - 1, Math.max(0, (Number(params.get('passo')) || 1) - 1))
  const setStep = (n: number) => setParams((p) => { const q = new URLSearchParams(p); q.set('passo', String(n + 1)); return q }, { replace: true })

  const optionsQ = useBuilderOptions(companyId, renew)
  const draftQ = useDraft(companyId, draftId)
  const assetsQ = useAssets(companyId)
  const create = useCreateDraft(companyId)
  const save = useSaveDraft(companyId)
  const publish = usePublish(companyId)

  const [bp, setBp] = useState<BlueprintDto | null>(null)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [reviewing, setReviewing] = useState(false)
  const [previewTab, setPreviewTab] = useState<'ig' | 'google'>('ig')

  // Carrega o rascunho (de ideias, do piloto ou salvo) ou monta um novo.
  useEffect(() => {
    if (draftId) {
      if (draftQ.data && loadedFor !== draftId) {
        setBp(draftQ.data.blueprint)
        setLoadedFor(draftId)
        setDirty(false)
      }
      return
    }
    if (loadedFor !== 'new' && (optionsQ.data || optionsQ.isError)) {
      const r = optionsQ.data?.renew
      setBp(initialBlueprint({
        goal: optionsQ.data?.goal ?? company.goal,
        segment: optionsQ.data?.segment ?? company.segment,
        city: optionsQ.data?.city ?? company.city ?? null,
        offer: offerParam ?? r?.body ?? r?.headline ?? '',
        headline: r?.headline ?? null,
        body: r?.body ?? null,
        name: r ? `Renovação: ${r.name}` : offerParam ? offerParam.slice(0, 60) : null,
      }))
      setLoadedFor('new')
      setDirty(true)
    }
  }, [draftId, draftQ.data, optionsQ.data, optionsQ.isError, loadedFor, offerParam, company.goal, company.segment, company.city])

  const draft: DraftDto | undefined = draftQ.data
  const version = draft?.updatedAt ?? ''
  const validationQ = useValidate(companyId, draftId, step === STEPS.length - 1 && !dirty && draft?.status !== 'publishing', version)
  const estimateQ = useEstimate(companyId, draftId, step >= 3 && !dirty, version)

  const update = (fn: (b: BlueprintDto) => BlueprintDto) => {
    setBp((b) => (b ? fn(b) : b))
    setDirty(true)
  }

  const saving = create.isPending || save.isPending

  /** Grava o rascunho (cria no primeiro "Continuar"). Devolve o id ou null em caso de erro. */
  const persist = async (nextStep: number): Promise<string | null> => {
    if (!bp) return null
    try {
      if (!draftId) {
        const d = await create.mutateAsync(toPatch(bp))
        setDirty(false)
        navigate(`/app/${companyId}/criar/${d.id}?passo=${nextStep + 1}`, { replace: true })
        return d.id
      }
      if (dirty) {
        await save.mutateAsync({ id: draftId, blueprint: toPatch(bp) })
        setDirty(false)
      }
      return draftId
    } catch (err) {
      toast(errorMessage(err))
      return null
    }
  }

  const go = async (n: number) => {
    if (n === step) return
    if (canEdit && (dirty || !draftId)) {
      const id = await persist(n)
      if (!id) return
      if (!draftId) { window.scrollTo({ top: 0, behavior: 'smooth' }); return } // a navegação já levou ao passo
    }
    setStep(n)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const doPublish = async () => {
    const id = await persist(step)
    if (!id) return
    try {
      await publish.mutateAsync(id)
      setReviewing(false)
      toast('Campanha enviada para as plataformas.')
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) void validationQ.refetch()
      toast(errorMessage(err))
    }
  }

  const imageUrl = useMemo(() => assetsQ.data?.items.find((a) => a.id === bp?.creative.imageAssetId)?.url ?? null, [assetsQ.data, bp?.creative.imageAssetId])

  // ---------- estados de tela ----------
  if (!canEdit && !draftId) {
    return (
      <>
        <PageHead title="Criar anúncio" />
        <Empty title="Você não tem permissão para criar campanhas">Peça ao gestor da empresa para liberar o acesso.</Empty>
      </>
    )
  }
  if (draftId && draftQ.isLoading) return <><PageHead title="Criar anúncio" /><div className="card"><Loading lines={6} /></div></>
  if (draftId && draftQ.isError) {
    const notFound = draftQ.error instanceof ApiError && draftQ.error.status === 404
    return (
      <>
        <PageHead title="Criar anúncio" />
        {notFound
          ? <Empty title="Rascunho não encontrado"><Link className="btn primary" to={`/app/${companyId}/criar`}>Começar um novo</Link></Empty>
          : <ErrorState error={draftQ.error} onRetry={() => draftQ.refetch()} />}
      </>
    )
  }
  if (draft && (draft.status === 'publishing' || draft.status === 'published' || (draft.status === 'failed' && !reviewing))) {
    return (
      <PublishStatus
        draft={draft}
        companyId={companyId}
        canPublish={canPublish}
        retrying={publish.isPending}
        onRetry={() => publish.mutate(draft.id, { onError: (err) => toast(errorMessage(err)) })}
        onReview={() => { setReviewing(true); setStep(STEPS.length - 1) }}
      />
    )
  }
  if (!bp) return <><PageHead title="Criar anúncio" /><div className="card"><Loading lines={6} /></div></>

  if (!canEdit) {
    return (
      <>
        <PageHead title={bp.name} sub="Rascunho de campanha (somente leitura)." />
        <Empty title="Você pode ver este rascunho, mas não editar">Peça ao gestor da empresa para revisar e publicar.</Empty>
      </>
    )
  }

  const options = optionsQ.data
  const targets = targetPlatforms(bp, options)
  const fromSuggestion = draft && draft.source !== 'user'
  const last = step === STEPS.length - 1
  const est = estimateQ.data
  const days = daysOf(bp)
  const total = bp.budget.dailyBudget * days
  const estText = estimateText(est, bp)
  const props = { bp, update, options, companyId }

  return (
    <>
      <PageHead
        title={fromSuggestion ? `Revisar: ${bp.name}` : 'Criar anúncio'}
        sub={fromSuggestion
          ? 'O Seu Impulso já preencheu tudo com base no que funciona para você. Revise e ajuste o que quiser.'
          : 'Responda 5 perguntas simples. O Seu Impulso monta a campanha no Google, no Instagram/Facebook, no TikTok e no ChatGPT por você.'}
      />

      {!draftId && <DraftsList companyId={companyId} canEdit={canEdit} />}

      {optionsQ.isError && <div className="form-error" style={{ marginBottom: 16 }}>Não conseguimos ver suas contas conectadas agora. Você pode continuar e conferir antes de publicar.</div>}
      {draft?.status === 'failed' && reviewing && (
        <div className="callout amber" style={{ marginBottom: 16 }}>
          <span>A última publicação não terminou: {draft.publishResult?.error ?? 'houve um erro'}. Ajuste o que precisar e publique de novo (o que já foi criado é reaproveitado).</span>
        </div>
      )}

      <nav className="steps" style={{ marginBottom: 20 }} aria-label="Passos">
        {STEPS.map((s, i) => (
          <span key={s} className="row" style={{ gap: 6 }}>
            {i > 0 && <span className="step-sep" />}
            <button type="button" className={`step ${i === step ? 'on' : ''} ${i < step ? 'done' : ''}`} style={{ background: 'none', border: 'none', padding: 0 }}
              aria-current={i === step ? 'step' : undefined} disabled={saving || (!draftId && i > step)} onClick={() => void go(i)}>
              <span className="n">{i < step ? <Check size={13} /> : i + 1}</span>
              <span className="hide-xs">{s}</span>
            </button>
          </span>
        ))}
      </nav>

      <div className="grid g-main" style={{ alignItems: 'start' }}>
        <div className="card" style={{ minWidth: 0 }}>
          {step === 0 && <GoalStep {...props} />}
          {step === 1 && <AudienceStep {...props} />}
          {step === 2 && <AdStep {...props} previewPlatform={previewTab === 'google' ? 'google_ads' : 'meta'} canEdit={canEdit} />}
          {step === 3 && <BudgetStep {...props} />}
          {step === 4 && (
            <ReviewStep
              bp={bp}
              onEdit={(s) => void go(s)}
              onRename={(name) => update((b) => ({ ...b, name }))}
              onCommit={() => { if (dirty) void persist(step) }}
              validation={dirty ? undefined : validationQ.data}
              validating={validationQ.isFetching || dirty}
              validationError={validationQ.error}
              onRetryValidation={() => void validationQ.refetch()}
              estimate={est}
              canPublish={canPublish}
            />
          )}

          <div className="divider" />
          <div className="row between wrap" style={{ gap: 8 }}>
            {step > 0
              ? <button type="button" className="btn" onClick={() => void go(step - 1)} disabled={saving}><ArrowLeft size={16} /> Voltar</button>
              : <Link to={`/app/${companyId}/campanhas`} className="btn ghost">Cancelar</Link>}
            {!last ? (
              <button type="button" className="btn primary" onClick={() => void go(step + 1)} disabled={saving}>
                {saving ? <RefreshCw size={16} className="spin" /> : null} Continuar <ArrowRight size={16} />
              </button>
            ) : (
              <div className="row wrap" style={{ gap: 8 }}>
                <button type="button" className={`btn ${canPublish ? 'ghost' : 'primary'}`} disabled={saving || (!dirty && !!draftId)}
                  onClick={async () => { const id = await persist(step); if (id) toast('Rascunho salvo.') }}>
                  <Save size={16} /> {dirty || !draftId ? 'Salvar rascunho' : 'Rascunho salvo'}
                </button>
                {canPublish && (
                  <button type="button" className="btn primary lg" onClick={() => void doPublish()}
                    // Com edição pendente o clique salva antes de publicar (e o servidor valida de novo).
                    disabled={saving || publish.isPending || (!dirty && !validationQ.data?.ok)}
                    title={!dirty && !validationQ.data?.ok ? 'Corrija as verificações antes de publicar' : undefined}>
                    {publish.isPending ? <RefreshCw size={16} className="spin" /> : null} Publicar campanha
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="stack" style={{ position: 'sticky', top: 84, minWidth: 0 }}>
          <div className="card">
            <div className="row between wrap" style={{ marginBottom: 14, gap: 8 }}>
              <h3>Prévia</h3>
              <Tabs value={previewTab} onChange={setPreviewTab} options={[{ value: 'ig', label: 'Instagram' }, { value: 'google', label: 'Google' }]} />
            </div>
            {previewTab === 'ig'
              ? (bp.creative.metaPost
                ? <InstagramPreview businessName={options?.companyName ?? company.name} headline="" body={bp.creative.metaPost.text ?? ''} imageUrl={bp.creative.metaPost.imageUrl ?? null} goal={bp.goal} />
                : <InstagramPreview businessName={options?.companyName ?? company.name} headline={bp.creative.headline} body={bp.creative.body} imageUrl={imageUrl} goal={bp.goal} />)
              : <GooglePreview businessName={options?.companyName ?? company.name} headline={bp.creative.headline} body={bp.creative.body} website={bp.destination?.url || options?.website || null} goal={bp.goal} />}
            {!targets.includes(previewTab === 'ig' ? 'meta' : 'google_ads') && (
              <p className="small muted mt-8">Com a divisão escolhida, este anúncio não vai para {previewTab === 'ig' ? 'o Instagram/Facebook' : 'o Google'}.</p>
            )}
          </div>
          <div className="card">
            <h3>Estimativa</h3>
            <div className="row between mt-12"><span className="muted">Investimento</span><b>{bp.budget.endDate ? `${brl(total)} no período` : `${brl(total)}/mês`}</b></div>
            {estText && <div className="row between mt-8"><span className="muted">Resultados esperados</span><b style={{ textAlign: 'right' }}>{estText}</b></div>}
            {est && est.costPerResultLow != null && est.costPerResultHigh != null && (
              <div className="row between mt-8"><span className="muted">Custo por resultado</span><b>{brl(est.costPerResultLow)} a {brl(est.costPerResultHigh)}</b></div>
            )}
            <p className="small muted mt-12">
              {step < 3 ? 'A estimativa de resultados aparece quando você chegar ao orçamento.'
                : estimateQ.isFetching || dirty ? 'Atualizando a estimativa ao salvar…'
                : !estText ? 'Ainda não temos dados suficientes para estimar resultados. Isso melhora depois das primeiras semanas.'
                : est?.basis === 'history' ? 'Estimativas usam o histórico das suas contas.' : 'Estimativas usam negócios parecidos na sua região.'}
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
